import { computed, reactive, ref, watch } from "vue";
import { defineStore } from "pinia";
import { useFlash } from "#ce/composables/useFlash";
import { createCanonicalUpdateApi } from "#ce/apps/SpAdmin/SpCanonicalUpdateApp/api/canonical-update";
import { useRowSelection } from "#ce/apps/SpAdmin/shared/composables/useRowSelection";
import { deepMerge, parseJson } from "#ce/apps/SpAdmin/shared/utils/config";
import { pluralizeTemplate } from "#ce/apps/SpAdmin/shared/utils/pluralize";
import { pollPreviewStatus } from "#ce/apps/SpAdmin/shared/utils/preview-poll";
import {
  formatProgressText,
  formatRemainingText,
  progressPercent,
} from "#ce/apps/SpAdmin/shared/utils/preview-progress";

export {
  PREVIEW_POLL_INTERVAL_MS,
  PREVIEW_POLL_MAX_ATTEMPTS,
  PREVIEW_POLL_MAX_TRANSIENT_FAILURES,
} from "#ce/apps/SpAdmin/shared/utils/preview-poll";

// Rows the apply will act on; skipped and error rows are display-only, and so are the rows of the
// rejected-terms list, which record Sparkle's decisions on requests.
const ACTIONABLE_STATUSES = ["new", "updated", "removed"];

export function isActionableRow(row) {
  return ACTIONABLE_STATUSES.includes(row.status) && row.sheet !== "rejected";
}

const SUMMARY_ORDER = ["new", "updated", "removed", "rejected", "deferred", "skipped", "unchanged", "errors"];

function defaultConfig() {
  return {
    previewUrl: "",
    statusUrl: "",
    processUrl: "",
    modalTitle: "Update from Canonical",
    statusLabels: "",
    labels: "{}",
  };
}

// Minimal English defaults so specs and Storybook work standalone; production
// always deep-merges the helper's localized `labels` JSON over these.
function labelDefaults() {
  return {
    confirm: "Apply Updates",
    cancel: "Cancel",
    loading: "Comparing against the canonical source…",
    progress: {
      rows: "%{processed} of %{total} rows compared",
      remaining: "About %{time} remaining",
      minutes: { one: "%{count} minute", other: "%{count} minutes" },
      seconds: { one: "%{count} second", other: "%{count} seconds" },
    },
    emptyState: "Everything is already in sync with the canonical source.",
    noActionableNote: "There is nothing to apply — the rows below are informational only.",
    rejectedNote: "Changes from the rejected-terms sheet are included in this review.",
    sourceNote: "",
    deferredNote: "Unchecked rows are left for a later review.",
    selectionNote:
      "Unchecked rows are rejected: new terms are recorded as rejected, and changed or removed terms are placed under client control.",
    rejectExcludedLabel: "Reject the unchecked rows",
    topLevelErrors: "Import errors",
    removedValue: "Removed: %{name}",
    columns: {
      status: "Status",
      name: "Name",
      type: "Type",
      changes: "Changes",
      reason: "Reason",
    },
    skippedHeading: "Skipped rows",
    noticesHeading: "Rows with notices",
    legendHeading: "Legend",
    attributes: {},
    selection: {
      selectAll: "Include all rows",
      includeRow: "Include %{name}",
      selectedCount: "%{count} of %{total} selected",
    },
    summary: {
      new: { one: "%{count} new term will be created.", other: "%{count} new terms will be created." },
      updated: { one: "%{count} term will be updated.", other: "%{count} terms will be updated." },
      removed: {
        one: "%{count} term missing from the canonical source will be archived.",
        other: "%{count} terms missing from the canonical source will be archived.",
      },
      rejected: { one: "%{count} term will be rejected.", other: "%{count} terms will be rejected." },
      deferred: {
        one: "%{count} term is left for a later review.",
        other: "%{count} terms are left for a later review.",
      },
      skipped: { one: "%{count} term will be skipped.", other: "%{count} terms will be skipped." },
      unchanged: { one: "%{count} term is already in sync.", other: "%{count} terms are already in sync." },
      errors: { one: "%{count} row has errors.", other: "%{count} rows have errors." },
    },
    errors: {
      previewFailed: "The comparison against the canonical source could not be completed.",
      processFailed: "The update could not be queued.",
    },
    flash: {
      queued: "The update from the canonical source has been queued. Terms will be updated in the background.",
      viewImport: "View import",
    },
  };
}

function emptySummary() {
  return { new: 0, updated: 0, unchanged: 0, skipped: 0, removed: 0, errors: 0, total: 0 };
}

// A name normalized the way the backend normalizes an exclusion (`ImportTemplate.clean_cell`):
// non-breaking spaces as spaces, one pair of surrounding double quotes dropped, trimmed,
// case-insensitive.
function normalizeName(name) {
  return String(name ?? "")
    .replace(/\u00a0/g, " ")
    .trim()
    .replace(/^"(.*)"$/s, "$1")
    .trim()
    .toLowerCase();
}

// Selection identity, the granularity the backend excludes by: a specialty row carries the
// canonical entry it stands for, which a renamed record and a new entry carrying its old name
// never share; a term row is identified by its name.
export function rowIdentity(row) {
  return row.canonical_key ?? row.name;
}

// Selection key: the normalized identity, so rows the backend would reject together share one
// checkbox.
export function selectionKey(row) {
  return normalizeName(rowIdentity(row));
}

// Render identity; the loop index prefix disambiguates a term that appears in more than
// one row.
export function rowKey(row) {
  return `${row.type}:${selectionKey(row)}`;
}

export const useCanonicalUpdateStore = defineStore("canonicalUpdate", () => {
  const api = createCanonicalUpdateApi();
  const flash = useFlash();
  // Explicit per-row approval over the preview, keyed by selectionKey (the exclusion
  // granularity the backend supports). Only actionable rows participate;
  // unchecked rows become excluded_names.
  const selection = useRowSelection();

  const config = reactive(defaultConfig());

  // Guards against stale async responses: an in-flight preview/confirm started
  // before reset()/close() (or superseded by a newer request) must not apply
  // its response over newer state. Non-reactive — only compared, never rendered.
  let requestGeneration = 0;

  // STATE:
  const isOpen = ref(false);
  const isLoading = ref(false);
  const previewData = ref(null);
  const previewToken = ref("");
  const isSubmitting = ref(false);
  const errorMessage = ref("");
  // { processed, total, estimatedSecondsRemaining } while the background comparison
  // reports row progress; null before the job starts its row loop.
  const previewProgress = ref(null);
  // Whether the apply rejects the unchecked rows, an outcome the import records, instead of
  // leaving them for a later review. Decided per apply, so it resets with the workflow state.
  const rejectExcluded = ref(false);

  // COMPUTED:
  const statusLabels = computed(() => ({
    new: "New term, will be created",
    updated: "Existing term, will be updated",
    removed: "Missing from the canonical source, will be archived",
    skipped: "Will be skipped",
    unchanged: "Already in sync, nothing will change",
    warning: "Has a notice, shown in the Changes column",
    error: "Has errors, will not be applied",
    ...parseJson(config.statusLabels, {}),
  }));
  // Localized UI copy from the helper, deep-merged over the English defaults.
  const labels = computed(() => deepMerge(labelDefaults(), parseJson(config.labels, {})));
  const summary = computed(() => ({ ...emptySummary(), ...(previewData.value?.summary ?? {}) }));
  const rows = computed(() => previewData.value?.rows ?? []);
  const actionableRows = computed(() => rows.value.filter(isActionableRow));
  // Skipped rows are listed apart from the review, with their reason: nothing about them is decided.
  const skippedRows = computed(() => rows.value.filter((row) => row.status === "skipped"));
  const reviewRows = computed(() => rows.value.filter((row) => row.status !== "skipped"));
  // Rows the rejected-terms import acts on whatever the selection: they take no checkbox.
  const rejectedListRows = computed(() =>
    rows.value.filter((row) => row.sheet === "rejected" && ACTIONABLE_STATUSES.includes(row.status)),
  );
  // The actionable rows the user kept checked; the rest become excluded_names.
  const includedRows = computed(() => actionableRows.value.filter((row) => selection.isSelected(selectionKey(row))));
  // One identity per unchecked selection key, in the spelling of the first row carrying it.
  const excludedNames = computed(() => {
    const names = new Map();

    for (const row of actionableRows.value) {
      const key = selectionKey(row);

      if (!selection.isSelected(key) && !names.has(key)) {
        names.set(key, rowIdentity(row));
      }
    }

    return [...names.values()];
  });
  // What unchecking means under the current toggle, shown above the table.
  const uncheckedRowsNote = computed(() =>
    rejectExcluded.value ? labels.value.selectionNote : labels.value.deferredNote,
  );
  const requestInFlight = computed(() => isLoading.value || isSubmitting.value);
  // A review with every actionable row unchecked can still be applied: each rejection is an
  // outcome the import records. The rejected-terms list's rows are work too, checkbox or not.
  const applyHasWork = computed(() => actionableRows.value.length > 0 || rejectedListRows.value.length > 0);
  const confirmDisabled = computed(() => requestInFlight.value || !applyHasWork.value);
  // Unique selection keys, the selection unit, so duplicate rows for one term do not skew the total.
  const selectableNameCount = computed(() => new Set(actionableRows.value.map(selectionKey)).size);
  const selectionSummary = computed(() =>
    String(labels.value.selection.selectedCount ?? "")
      .replaceAll("%{count}", selection.selectedCount.value)
      .replaceAll("%{total}", selectableNameCount.value),
  );
  // The summary's counts as the selection leaves them. An actionable status counts the rows the
  // apply acts on, the rejected-terms list's included; every unchecked actionable row counts as
  // rejected under the toggle and as left for a later review otherwise. The other statuses take
  // no checkbox, so their counts are the server's.
  const outcomeCounts = computed(() => {
    const unchecked = actionableRows.value.length - includedRows.value.length;
    const counts = { ...summary.value, rejected: 0, deferred: 0 };
    counts[rejectExcluded.value ? "rejected" : "deferred"] = unchecked;
    const appliedRows = [...includedRows.value, ...rejectedListRows.value];

    for (const status of ACTIONABLE_STATUSES) {
      counts[status] = appliedRows.filter((row) => row.status === status).length;
    }

    return counts;
  });
  // Summary fragments composed from the server-provided pluralization
  // templates; zero counts stay out of the sentence.
  const summaryParts = computed(() => {
    const templates = labels.value.summary;

    return SUMMARY_ORDER.filter((key) => outcomeCounts.value[key] > 0).map((key) =>
      pluralizeTemplate(templates[key], outcomeCounts.value[key]),
    );
  });
  const summaryText = computed(() => summaryParts.value.join(" "));
  const progressPercentValue = computed(() => progressPercent(previewProgress.value));
  const progressText = computed(() => formatProgressText(previewProgress.value, labels.value.progress));
  const remainingText = computed(() =>
    formatRemainingText(previewProgress.value?.estimatedSecondsRemaining, labels.value.progress),
  );

  // Every actionable row starts approved whenever preview data arrives or clears. Sync flush
  // so the selection is consistent immediately after previewData is assigned.
  function resetSelection() {
    selection.reset(actionableRows.value.map(selectionKey));
  }

  watch(previewData, resetSelection, { flush: "sync" });

  // ACTIONS:
  function configure(nextConfig = {}) {
    Object.assign(config, defaultConfig(), nextConfig);
  }

  function reset() {
    requestGeneration += 1;
    previewData.value = null;
    previewToken.value = "";
    isLoading.value = false;
    isSubmitting.value = false;
    errorMessage.value = "";
    previewProgress.value = null;
    rejectExcluded.value = false;
  }

  // There is no input step: opening immediately fetches the preview.
  function open() {
    reset();
    isOpen.value = true;

    return preview();
  }

  function close() {
    isOpen.value = false;
    reset();
  }

  function applyPreviewData(data) {
    previewData.value = {
      rows: data.rows || [],
      summary: { ...emptySummary(), ...(data.summary || {}) },
      errors: data.errors || [],
    };
  }

  async function preview() {
    requestGeneration += 1;
    const generation = requestGeneration;
    isLoading.value = true;
    errorMessage.value = "";
    previewToken.value = "";

    try {
      const started = await api.preview(config.previewUrl);

      if (generation !== requestGeneration) {
        return false;
      }

      // A 422 start returns the rows/summary payload directly (top-level
      // errors) instead of a token — render it without polling.
      if (Array.isArray(started?.rows)) {
        applyPreviewData(started);
        return true;
      }

      if (!started?.token) {
        errorMessage.value = labels.value.errors.previewFailed;
        return false;
      }

      previewToken.value = started.token;

      // The generation guard covers the whole poll: a response landing after
      // close() or a newer preview is discarded, not applied.
      const result = await pollPreviewStatus({
        fetchStatus: () => api.previewStatus(config.statusUrl, started.token),
        isCurrent: () => generation === requestGeneration,
        onProgress: (progress) => {
          previewProgress.value = progress;
        },
      });

      if (result.outcome === "ready") {
        applyPreviewData(result.data);
        return true;
      }

      if (result.outcome === "stale") {
        return false;
      }

      // failed, expired, cancelled, or the poll cap reached.
      errorMessage.value = result.errors.join(", ") || labels.value.errors.previewFailed;
      return false;
    } catch (error) {
      if (generation !== requestGeneration) {
        return false;
      }

      errorMessage.value = error.message || labels.value.errors.previewFailed;
      return false;
    } finally {
      // A stale request must not clear a newer request's loading state.
      if (generation === requestGeneration) {
        isLoading.value = false;
      }
    }
  }

  async function confirm() {
    requestGeneration += 1;
    const generation = requestGeneration;
    isSubmitting.value = true;
    errorMessage.value = "";

    try {
      // The backend applies every actionable change from the reviewed snapshots except the
      // excluded names, so an all-checked review posts an empty list.
      const data = await api.process(config.processUrl, {
        token: previewToken.value,
        excluded_names: excludedNames.value,
        reject_excluded: rejectExcluded.value,
      });

      if (generation !== requestGeneration) {
        return false;
      }

      // queued:false includes an expired preview token (the review sat open
      // too long, or was already applied); the modal stays open so the user
      // can close and re-open to re-preview.
      if (!data?.queued) {
        errorMessage.value = (data?.errors ?? []).join(", ") || labels.value.errors.processFailed;
        return false;
      }

      // The apply runs in the background, so the page keeps showing pre-update
      // data either way — no reload, just the queued acknowledgement (a
      // client-side <sp-alert> a reload would destroy), linked to the Data Import
      // the run fills.
      const flashOptions = { autoremoveDelay: 8000 };

      if (data.bulk_import_url) {
        flashOptions.action = { href: data.bulk_import_url, label: labels.value.flash.viewImport };
      }

      flash.success(labels.value.flash.queued, flashOptions);
      close();
      return true;
    } catch (error) {
      if (generation !== requestGeneration) {
        return false;
      }

      errorMessage.value = error.message || labels.value.errors.processFailed;
      return false;
    } finally {
      // A stale request must not clear a newer request's submitting state. (The
      // success path bumps the generation itself via close() → reset(), which
      // also already reset isSubmitting.)
      if (generation === requestGeneration) {
        isSubmitting.value = false;
      }
    }
  }

  return {
    config,
    isOpen,
    isLoading,
    previewData,
    previewToken,
    isSubmitting,
    errorMessage,
    previewProgress,
    rejectExcluded,
    statusLabels,
    labels,
    uncheckedRowsNote,
    summary,
    rows,
    actionableRows,
    reviewRows,
    skippedRows,
    rejectedListRows,
    applyHasWork,
    includedRows,
    excludedNames,
    requestInFlight,
    confirmDisabled,
    summaryParts,
    summaryText,
    selectionSummary,
    selectedRowKeys: selection.selectedKeys,
    allRowsSelected: selection.allSelected,
    selectionIndeterminate: selection.indeterminate,
    selectedRowCount: selection.selectedCount,
    isRowSelected: (name) => selection.isSelected(normalizeName(name)),
    toggleRow: (name) => selection.toggle(normalizeName(name)),
    toggleAllRows: selection.toggleAll,
    progressPercent: progressPercentValue,
    progressText,
    remainingText,
    configure,
    reset,
    open,
    close,
    preview,
    confirm,
  };
});
